import validator from "validator";

/**
 * The FunctionHelper bundles useful static functions
 */
export default abstract class FunctionHelper {
	/**
	 * The given function can only be called every limit (in millis). Returns the new throttled function.
	 */
	public static throttle(func: (...args: any) => any, limit: number = 300): (...args: any) => any {
		let inThrottle: boolean;
		let lastCall: ((...args: any) => any) | null = null;
		let lastCallArgs: IArguments | Array<any> = [];

		return function (): any {
			if (!inThrottle) {
				func(...arguments);

				inThrottle = true;
				lastCall = null;
				lastCallArgs = [];

				window.setTimeout(() => {
					inThrottle = false;

					if (lastCall !== null) {
						lastCall(...lastCallArgs);
					}
				}, limit);
			} else {
				lastCall = func;
				lastCallArgs = arguments;
			}
		};
	}

	/**
	 * Blocks calls to the given function when the given wait timeout (in millis) is active. In difference to throttle
	 * further calls in the wait timeout will reset the timeout. Returns the new deferred function.
	 */
	public static defer(func: (...args: any) => any, wait: number = 300): (...args: any) => any {
		let timeout: number;
		let isDeferred: boolean = false;

		return function (): any {
			if (isDeferred) {
				window.clearTimeout(timeout);
			}

			isDeferred = true;
			timeout = window.setTimeout(() => {
				isDeferred = false;
				func(...arguments);
			}, wait);
		};
	}

	/**
	 * Blocks calls to the given function if it is not finished yet. Returns the new limited function.
	 */
	public static once(func: (...args: any) => Promise<any>): (this: any) => Promise<any> {
		let isRunning: boolean = false;

		return async (): Promise<any> => {
			if (isRunning) {
				return;
			}

			isRunning = true;
			await func(...arguments);
			isRunning = false;
		};
	}

	/**
	 * Checks if the given value is undefined or empty.
	 */
	public static isEmpty(value: any): boolean {
		if (!FunctionHelper.exists(value)) {
			return true;
		}

		if (typeof value === "object") {
			return Object.keys(value).length === 0;
		}

		if (Array.isArray(value)) {
			return value.length === 0;
		}

		if (typeof value === "number") {
			return isNaN(value);
		}

		if (typeof value !== "string") {
			value = value + "";
		}

		return validator.isEmpty(FunctionHelper.applyValue(value, ""), {ignore_whitespace: true});
	}

	/**
	 * Checks if the given value is defined.
	 */
	public static exists(value: any): boolean {
		return typeof value !== "undefined" && value !== null;
	}

	/**
	 * Returns the first defined given value
	 */
	public static applyValue(...values: any): any {
		for (let value of values) {
			if (FunctionHelper.exists(value)) {
				return value;
			}
		}
	}

	/**
	 * Returns the first not empty string value
	 */
	public static applyStringValue(...values: any): string {
		for (let value of values) {
			if (!FunctionHelper.isEmpty(value)) {
				return value + "";
			}
		}

		return "";
	}
}